import {
  normalizeSearch,
  type PortCallActivity,
  type PortCallLocationType,
  type PortCallStatus,
  parsePortCall,
} from '@felix/domain';
import type { PortCall } from './types.js';

/**
 * Liman aramaları ekranının saf parçaları (M13f; ADR 0052): yol, süzgeç, durum sayıları, acente
 * önerisi ve form dönüşümü. Formun doğrulaması gemi sunucusuyla aynı şemadan (`parsePortCall`).
 */

export const PORT_CALLS_PATH = '/operations/port-calls';

export type PortCallRoute =
  | { view: 'list' }
  | { view: 'new' }
  | { view: 'detail'; id: string }
  | { view: 'edit'; id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Görünüm sorgu dizesinden (`?new=1`, `?id=…`, `?id=…&edit=1`). */
export function portCallRoute(search: string): PortCallRoute {
  const params = new URLSearchParams(search);
  const id = params.get('id');
  if (id && UUID.test(id)) {
    return params.get('edit') === '1' ? { view: 'edit', id } : { view: 'detail', id };
  }
  return params.get('new') === '1' ? { view: 'new' } : { view: 'list' };
}

export function portCallPath(route: PortCallRoute): string {
  switch (route.view) {
    case 'list':
      return PORT_CALLS_PATH;
    case 'new':
      return `${PORT_CALLS_PATH}?new=1`;
    case 'detail':
      return `${PORT_CALLS_PATH}?id=${route.id}`;
    case 'edit':
      return `${PORT_CALLS_PATH}?id=${route.id}&edit=1`;
  }
}

/** Anın limandaki yerel günü (`YYYY-MM-DD`). */
export function localDay(iso: string, tzOffsetMin: number): string {
  return new Date(Date.parse(iso) + tzOffsetMin * 60_000).toISOString().slice(0, 10);
}

/** Aramanın günü: gerçek varış varsa o, yoksa ETA; limanın yerel günü. */
export function callDay(call: Pick<PortCall, 'eta' | 'ata' | 'tzOffsetMin'>): string {
  return localDay(call.ata ?? call.eta, call.tzOffsetMin);
}

export type StatusTab = 'all' | PortCallStatus;
export const STATUS_TABS: readonly StatusTab[] = [
  'all',
  'planned',
  'ongoing',
  'completed',
  'cancelled',
];

export interface PortCallFilter {
  vesselId: string | null;
  status: StatusTab;
  /** Liman, ülke kodu, gemi, acente ya da sefer no. */
  query: string;
  from: string | null;
  to: string | null;
}

export const NO_PORT_CALL_FILTER: PortCallFilter = {
  vesselId: null,
  status: 'all',
  query: '',
  from: null,
  to: null,
};

function matches(call: PortCall, filter: Omit<PortCallFilter, 'status'>): boolean {
  if (filter.vesselId && call.vesselId !== filter.vesselId) return false;
  const day = callDay(call);
  if (filter.from && day < filter.from) return false;
  if (filter.to && day > filter.to) return false;
  const query = normalizeSearch(filter.query);
  if (!query) return true;
  return [
    call.portName,
    call.portCode,
    call.country,
    call.vesselName,
    call.agentName,
    call.voyageNo,
    call.berth,
  ].some((text) => text && normalizeSearch(text).includes(query));
}

export function filterPortCalls(items: readonly PortCall[], filter: PortCallFilter): PortCall[] {
  return items.filter(
    (call) => matches(call, filter) && (filter.status === 'all' || call.status === filter.status),
  );
}

/** Sekme sayıları: durum dışındaki süzgeçlere uyanlar. */
export function statusCounts(
  items: readonly PortCall[],
  filter: PortCallFilter,
): Record<StatusTab, number> {
  const counts: Record<StatusTab, number> = {
    all: 0,
    planned: 0,
    ongoing: 0,
    completed: 0,
    cancelled: 0,
  };
  for (const call of items) {
    if (!matches(call, filter)) continue;
    counts.all += 1;
    counts[call.status] += 1;
  }
  return counts;
}

export interface AgentSuggestion {
  agentName: string;
  agentContact: string | null;
  agentPhone: string | null;
  agentEmail: string | null;
}

/**
 * Acente önerisi (ADR 0052 §1; ayrı acente rehberi yok): aynı limandaki (kod, kodsuzsa ad)
 * önceki aramaların acenteleri, en son güncellenen önce, ada göre tekil.
 */
export function agentSuggestions(
  items: readonly PortCall[],
  portCode: string | null,
  portName: string,
): AgentSuggestion[] {
  const name = normalizeSearch(portName);
  const same = items
    .filter((call) =>
      portCode ? call.portCode === portCode : normalizeSearch(call.portName) === name,
    )
    .filter((call) => call.agentName)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const seen = new Set<string>();
  const result: AgentSuggestion[] = [];
  for (const call of same) {
    const key = normalizeSearch(call.agentName ?? '');
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      agentName: call.agentName ?? '',
      agentContact: call.agentContact,
      agentPhone: call.agentPhone,
      agentEmail: call.agentEmail,
    });
  }
  return result;
}

/* ---------- Form ---------- */

export interface PortCallFormValues {
  voyageId: string | null;
  itineraryKey: string | null;
  portName: string;
  portCode: string | null;
  country: string;
  locationType: PortCallLocationType;
  berth: string;
  /** UTC ISO ya da boş. */
  eta: string | null;
  etd: string | null;
  ata: string | null;
  atd: string | null;
  tzOffsetMin: number;
  agentName: string;
  agentContact: string;
  agentPhone: string;
  agentEmail: string;
  activities: PortCallActivity[];
  notes: string;
}

export function newPortCallValues(defaults: {
  tzOffsetMin: number;
  voyageId: string | null;
}): PortCallFormValues {
  return {
    voyageId: defaults.voyageId,
    itineraryKey: null,
    portName: '',
    portCode: null,
    country: '',
    locationType: 'berth',
    berth: '',
    eta: null,
    etd: null,
    ata: null,
    atd: null,
    tzOffsetMin: defaults.tzOffsetMin,
    agentName: '',
    agentContact: '',
    agentPhone: '',
    agentEmail: '',
    activities: [],
    notes: '',
  };
}

export function valuesOf(call: PortCall): PortCallFormValues {
  return {
    voyageId: call.voyageId,
    itineraryKey: call.itineraryKey,
    portName: call.portName,
    portCode: call.portCode,
    country: call.country,
    locationType: call.locationType,
    berth: call.berth ?? '',
    eta: call.eta,
    etd: call.etd,
    ata: call.ata,
    atd: call.atd,
    tzOffsetMin: call.tzOffsetMin,
    agentName: call.agentName ?? '',
    agentContact: call.agentContact ?? '',
    agentPhone: call.agentPhone ?? '',
    agentEmail: call.agentEmail ?? '',
    activities: [...call.activities],
    notes: call.notes ?? '',
  };
}

/** Formdan API girdisi (doğrulamayı şema yapar; boş alanlar null). */
export function formToInput(values: PortCallFormValues): Record<string, unknown> {
  const text = (value: string) => (value.trim() === '' ? null : value);
  return {
    voyageId: values.voyageId,
    itineraryKey: values.voyageId ? values.itineraryKey : null,
    portCode: values.portCode,
    portName: values.portName,
    country: values.country,
    locationType: values.locationType,
    berth: text(values.berth),
    eta: values.eta,
    etd: values.etd,
    ata: values.ata,
    atd: values.atd,
    tzOffsetMin: values.tzOffsetMin,
    agentName: text(values.agentName),
    agentContact: text(values.agentContact),
    agentPhone: text(values.agentPhone),
    agentEmail: text(values.agentEmail),
    activities: values.activities,
    notes: text(values.notes),
  };
}

/** Şemanın alan hataları: alan → ilk kod (`activities.1` → `activities`). */
export function formIssues(input: Record<string, unknown>): Record<string, string> {
  const parsed = parsePortCall(input);
  if (parsed.ok) return {};
  const issues: Record<string, string> = {};
  for (const issue of parsed.issues) {
    const field = issue.path.split('.')[0] ?? '';
    issues[field] ??= issue.code;
  }
  return issues;
}

/** Süre metni: `1 d 4 h`, `6.5 h`. */
export function durationText(hours: number, unit: { day: string; hour: string }): string {
  if (hours < 24) return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} ${unit.hour}`;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours - days * 24);
  return rest > 0 ? `${days} ${unit.day} ${rest} ${unit.hour}` : `${days} ${unit.day}`;
}
